/**
 * Per-client operator inbox (clients.client_threads + clients.client_messages).
 *
 * Wraps the `clients.rpc_client_inbox_*` SDFs introduced in
 * 20260601020000_clients_inbox_init.sql. RLS + side-effects (email
 * dispatch, SMS dispatch, audit) all live in the SDFs, so this module
 * is a pure typed wire-format layer.
 *
 * The companion UI lives in `packages/modules/clients` and is mounted
 * at `/clients/inbox` inside the back-office (`apps/web`).
 */

import { supabase } from '../client';
import { clampPageSize, type KeysetCursor } from '../pagination';

// =========================================================================
// Wire types -- mirror SDF return shapes one-for-one.
// =========================================================================

export type ClientInboxChannel = 'in_app' | 'email' | 'sms';
export type ClientInboxDirection = 'inbound' | 'outbound';
export type ClientInboxThreadStatus = 'open' | 'pending' | 'closed';

export interface ClientInboxThreadRow {
  id: string;
  org_id: string;
  store_id: string;
  client_id: string;
  client_display_name: string | null;
  client_email: string | null;
  client_phone: string | null;
  subject: string | null;
  status: ClientInboxThreadStatus;
  last_message_at: string;
  last_message_direction: ClientInboxDirection | null;
  last_message_channel: ClientInboxChannel | null;
  operator_last_read_at: string | null;
  /** Only present on list_threads rows; get_thread omits it. */
  unread_for_operator?: boolean;
  created_at: string;
}

/**
 * Snapshot of `core.sms_messages.status`. NULL for inbound and in_app
 * messages. Surfaced under outbound bubbles as a delivery receipt.
 *
 * The set is the check-constraint enum from
 * `20260525001500_sms_channel_init.sql` — keep in sync if that ever
 * changes.
 */
export type ClientInboxSmsDeliveryStatus =
  | 'queued'
  | 'sent'
  | 'delivered'
  | 'failed'
  | 'undelivered'
  | 'opted_out';

/**
 * Snapshot of `core.email_messages.status`. NULL for inbound, in_app,
 * and SMS messages.
 */
export type ClientInboxEmailDeliveryStatus =
  | 'queued'
  | 'sent'
  | 'delivered'
  | 'bounced'
  | 'complained'
  | 'failed'
  | 'opened'
  | 'clicked';

export interface ClientInboxMessageRow {
  id: string;
  thread_id: string;
  direction: ClientInboxDirection;
  channel: ClientInboxChannel;
  body: string;
  sender_user_id: string | null;
  sender_display_name: string | null;
  sender_email: string | null;
  sender_phone: string | null;
  email_message_id: string | null;
  sms_message_id: string | null;
  /**
   * Delivery state of the SMS this message was dispatched as. Surfaced
   * by `rpc_client_inbox_list_messages` from the LEFT JOIN to
   * `core.sms_messages` (added in
   * `20260628020200_clients_inbox_list_messages_with_delivery.sql`).
   * NULL for inbound, in_app, and email messages.
   */
  sms_status: ClientInboxSmsDeliveryStatus | null;
  /** Mirror of `sms_status` for the email channel. */
  email_status: ClientInboxEmailDeliveryStatus | null;
  meta: Record<string, unknown>;
  created_at: string;
}

export interface ClientInboxKeysetInput {
  cursor?: KeysetCursor | null;
  pageSize?: number;
}

export interface ClientInboxListResult<T> {
  rows: T[];
  nextCursor: KeysetCursor | null;
}

// =========================================================================
// SDF wrappers
// =========================================================================

function cursorToJson(cursor: KeysetCursor | null | undefined): unknown {
  if (!cursor) return null;
  return { sortValue: cursor.sortValue, id: cursor.id };
}

export interface ListClientInboxThreadsInput extends ClientInboxKeysetInput {
  storeId: string;
  status?: ClientInboxThreadStatus | null;
}

export async function listClientInboxThreads(
  input: ListClientInboxThreadsInput,
): Promise<ClientInboxListResult<ClientInboxThreadRow>> {
  const pageSize = clampPageSize(input.pageSize ?? 25);
  const { data, error } = await (supabase() as any)
    .schema('clients')
    .rpc('rpc_client_inbox_list_threads', {
      p_store_id: input.storeId,
      p_status: input.status ?? null,
      p_cursor: cursorToJson(input.cursor),
      p_limit: pageSize,
    });
  if (error) throw error;
  const rows = (data ?? []) as ClientInboxThreadRow[];
  let nextCursor: KeysetCursor | null = null;
  if (rows.length === pageSize) {
    const last = rows[rows.length - 1];
    if (last) nextCursor = { sortValue: last.last_message_at, id: last.id };
  }
  return { rows, nextCursor };
}

// =========================================================================
// Org-mode (cross-store) thread list. Wraps
// `clients.rpc_org_list_inbox_threads` (introduced in
// 20260628020900_clients_inbox_org_list_threads.sql).
//
// Returns one extra `store_name` column so the UI can disambiguate
// rows across stores under the same org. The page-size + cursor
// contract mirrors `listOrgClients` so the org-mode infinite-scroll
// helper can be reused unchanged.
// =========================================================================

export interface OrgClientInboxThreadRow extends ClientInboxThreadRow {
  store_name: string;
  unread_for_operator: boolean;
}

export interface ListOrgClientInboxThreadsInput {
  orgId: string;
  status?: ClientInboxThreadStatus | null;
  cursor?: KeysetCursor | null;
  pageSize?: number;
}

export async function listOrgClientInboxThreads(
  input: ListOrgClientInboxThreadsInput,
): Promise<ClientInboxListResult<OrgClientInboxThreadRow>> {
  const pageSize = clampPageSize(input.pageSize ?? 25);
  const { data, error } = await (supabase() as any)
    .schema('clients')
    .rpc('rpc_org_list_inbox_threads', {
      p_org_id: input.orgId,
      p_cursor: cursorToJson(input.cursor),
      p_page_size: pageSize,
      p_status: input.status ?? null,
    });
  if (error) throw error;
  const rows = (data ?? []) as OrgClientInboxThreadRow[];
  let nextCursor: KeysetCursor | null = null;
  if (rows.length === pageSize) {
    const last = rows[rows.length - 1];
    if (last) nextCursor = { sortValue: last.last_message_at, id: last.id };
  }
  return { rows, nextCursor };
}

export async function getClientInboxThread(
  threadId: string,
): Promise<ClientInboxThreadRow | null> {
  const { data, error } = await (supabase() as any)
    .schema('clients')
    .rpc('rpc_client_inbox_get_thread', { p_thread_id: threadId });
  if (error) throw error;
  const row = Array.isArray(data) ? data[0] : data;
  return (row as ClientInboxThreadRow | null) ?? null;
}

export interface ListClientInboxMessagesInput {
  threadId: string;
  /** Keyset cursor on (created_at asc, id asc) -- oldest-first. */
  cursor?: { sortValue: string; id: string } | null;
  pageSize?: number;
}

export async function listClientInboxMessages(
  input: ListClientInboxMessagesInput,
): Promise<{
  rows: ClientInboxMessageRow[];
  nextCursor: { sortValue: string; id: string } | null;
}> {
  const pageSize = clampPageSize(input.pageSize ?? 50);
  const { data, error } = await (supabase() as any)
    .schema('clients')
    .rpc('rpc_client_inbox_list_messages', {
      p_thread_id: input.threadId,
      p_cursor: input.cursor
        ? { sortValue: input.cursor.sortValue, id: input.cursor.id }
        : null,
      p_limit: pageSize,
    });
  if (error) throw error;
  const rows = (data ?? []) as ClientInboxMessageRow[];
  let nextCursor: { sortValue: string; id: string } | null = null;
  if (rows.length === pageSize) {
    const last = rows[rows.length - 1];
    if (last) nextCursor = { sortValue: last.created_at, id: last.id };
  }
  return { rows, nextCursor };
}

export interface SendClientInboxMessageInput {
  threadId: string;
  channel: ClientInboxChannel;
  body: string;
}

export async function sendClientInboxMessage(
  input: SendClientInboxMessageInput,
): Promise<{ id: string }> {
  const { data, error } = await (supabase() as any)
    .schema('clients')
    .rpc('rpc_client_inbox_send_message', {
      p_thread_id: input.threadId,
      p_channel: input.channel,
      p_body: input.body,
    });
  if (error) throw error;
  const id = typeof data === 'string' ? data : (data as any)?.id ?? null;
  if (typeof id !== 'string') {
    throw new Error('rpc_client_inbox_send_message returned no id');
  }
  return { id };
}

export async function markClientInboxThreadRead(threadId: string): Promise<void> {
  const { error } = await (supabase() as any)
    .schema('clients')
    .rpc('rpc_client_inbox_mark_thread_read', { p_thread_id: threadId });
  if (error) throw error;
}

export async function setClientInboxThreadStatus(
  threadId: string,
  status: ClientInboxThreadStatus,
): Promise<void> {
  const { error } = await (supabase() as any)
    .schema('clients')
    .rpc('rpc_client_inbox_set_status', {
      p_thread_id: threadId,
      p_status: status,
    });
  if (error) throw error;
}

export async function ensureClientInboxThread(
  clientId: string,
  subject?: string | null,
): Promise<{ id: string }> {
  const { data, error } = await (supabase() as any)
    .schema('clients')
    .rpc('rpc_client_inbox_ensure_thread', {
      p_client_id: clientId,
      p_subject: subject ?? null,
    });
  if (error) throw error;
  const id = typeof data === 'string' ? data : (data as any)?.id ?? null;
  if (typeof id !== 'string') {
    throw new Error('rpc_client_inbox_ensure_thread returned no id');
  }
  return { id };
}

export async function clientInboxUnreadCount(storeId: string): Promise<number> {
  const { data, error } = await (supabase() as any)
    .schema('clients')
    .rpc('rpc_client_inbox_unread_count', { p_store_id: storeId });
  if (error) throw error;
  return typeof data === 'number' ? data : 0;
}

// =========================================================================
// Realtime: live INSERT events on `clients.client_messages` for one thread.
//
// The companion table was added to the `supabase_realtime` publication in
// migration `20260628020200_clients_inbox_list_messages_with_delivery.sql`.
// The tenant `client_messages_tenant_select` RLS policy gates the stream
// by `core.user_has_org_access` / `core.user_has_store_access`, so realtime
// only fans events to operators who can already SELECT the row — the
// service is a pure client-side `postgres_changes` subscription with no
// extra authz layer.
//
// Pattern mirrors `subscribeToOrgAppointments` in `./calendar.ts`. Returns
// `{ unsubscribe }` so React effects can tear down on unmount.
// =========================================================================

export interface ClientInboxRealtimeRow {
  id: string;
  thread_id: string;
  direction: ClientInboxDirection;
  channel: ClientInboxChannel;
  created_at: string;
}

export function subscribeToClientInboxThread(
  threadId: string,
  onInsert: (row: ClientInboxRealtimeRow) => void,
): { unsubscribe: () => void } {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const sb = supabase() as any;
  const channel = sb
    .channel(`clients.client_messages:thread:${threadId}`)
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'clients',
        table: 'client_messages',
        filter: `thread_id=eq.${threadId}`,
      },
      (payload: { new: ClientInboxRealtimeRow }) => {
        if (payload?.new) onInsert(payload.new);
      },
    )
    .subscribe();
  return {
    unsubscribe: () => {
      try {
        sb.removeChannel(channel);
      } catch {
        // best-effort tear-down; idempotent
      }
    },
  };
}
